import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import type { PersonnelVoluntaryWork } from '../personnelTypes';
import { sampleVoluntaryWork } from '../utils/sampleVoluntaryWork';

/**
 * Records or corrects one spell of voluntary work — CS Form 212, section VI.
 *
 * **The form is deliberately shorter than section V's**, and the omissions are the point: there
 * is no salary, no grade, no step and no status of appointment, because a volunteer is neither
 * paid nor appointed. Offering any of those would invite a reader to record a wage nobody was
 * paid. What section VI asks for instead is the number of hours rendered.
 *
 * **The address is one free-text line, not the `<AddressPicker>`.** That control is over the
 * PSGC catalog and belongs to a person's own present and permanent address; an organization
 * somebody volunteered at may be anywhere, and the form gives it one cell.
 */
export function EditVoluntaryWorkModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelVoluntaryWork | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [periodFrom, setPeriodFrom] = useState(editing?.periodFrom ?? '');
  const [periodTo, setPeriodTo] = useState(editing?.periodTo ?? '');
  const [organizationName, setOrganizationName] = useState(editing?.organizationName ?? '');
  const [organizationAddress, setOrganizationAddress] = useState(
    editing?.organizationAddress ?? '',
  );
  const [numberOfHours, setNumberOfHours] = useState(
    editing?.numberOfHours === null || editing?.numberOfHours === undefined
      ? ''
      : String(editing.numberOfHours),
  );
  const [positionOrNature, setPositionOrNature] = useState(editing?.positionOrNature ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const today = todayCalendarDate();

  const hoursValue = numberOfHours.trim() === '' ? null : Number(numberOfHours);
  const hoursIsNumber = hoursValue === null || Number.isInteger(hoursValue);
  const hoursNotNegative = hoursValue === null || hoursValue >= 0;

  const endsBeforeStart = periodTo !== '' && periodFrom !== '' && periodTo < periodFrom;

  const valid =
    organizationName.trim().length > 0 &&
    organizationAddress.trim().length > 0 &&
    positionOrNature.trim().length > 0 &&
    periodFrom.length > 0 &&
    hoursIsNumber &&
    hoursNotNegative &&
    !endsBeforeStart;

  const canSave = valid && confirmed && !saving;

  /** Fills every required field, so the primary action enables on the first try. */
  function generate() {
    const sample = sampleVoluntaryWork();

    setPeriodFrom(sample.periodFrom);
    setPeriodTo(sample.periodTo);
    setOrganizationName(sample.organizationName);
    setOrganizationAddress(sample.organizationAddress);
    setNumberOfHours(sample.numberOfHours);
    setPositionOrNature(sample.positionOrNature);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      periodFrom,

      // Empty is the form's PRESENT — a real answer meaning the involvement is still going, so
      // it travels as null rather than as today's date.
      periodTo: periodTo || null,
      organizationName: organizationName.trim(),
      organizationAddress: organizationAddress.trim(),

      // Null rather than 0 when the box is empty: "nobody counted the hours" and "served none"
      // are different facts, and this column is what the section is totalled on.
      numberOfHours: hoursValue,
      positionOrNature: positionOrNature.trim(),
    };

    try {
      if (editing) await personnelApi.updateVoluntaryWork(personnelId, editing.id, request);
      else await personnelApi.addVoluntaryWork(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      // Every server field name kebabs straight onto an input id here — `organizationName`
      // becomes `voluntary-work-organization-name` — so no `fieldIds` map is needed. There is
      // no `<RadioGroup>` on this form, which is the one control that needs one.
      focusValidationError(failure, { idPrefix: 'voluntary-work' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit voluntary work' : 'Add voluntary work'}
      size="2xl"
      id="voluntary-work-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'voluntary-work-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="voluntary-work-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="voluntary-work-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="voluntary-work-organization-name"
            label="Organization"
            required
            description="The civic, non-government, people's or voluntary organization, written in full as the form asks."
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            error={
              organizationName.trim()
                ? error?.fieldError('organizationName')
                : "Enter the organization's name."
            }
            disabled={saving}
          />

          <TextField
            id="voluntary-work-organization-address"
            label="Address"
            required
            /*
              The description says why this is a text box rather than the address picker used
              elsewhere on this record — a reader who met one control on the Address panel and
              a different one here should be told the difference is deliberate.
            */
            description="One line, as written. Not the address catalog: an organization may be anywhere, and CS Form 212 gives it one cell."
            value={organizationAddress}
            onChange={(e) => setOrganizationAddress(e.target.value)}
            error={
              organizationAddress.trim()
                ? error?.fieldError('organizationAddress')
                : "Enter the organization's address."
            }
            disabled={saving}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="voluntary-work-period-from"
            label="From"
            required
            max={today}
            value={periodFrom}
            onChange={setPeriodFrom}
            error={periodFrom ? error?.fieldError('periodFrom') : 'Choose the day it began.'}
            disabled={saving}
          />

          <DateField
            id="voluntary-work-period-to"
            label="To"
            max={today}
            description="Leave it empty if this involvement is still going."
            value={periodTo}
            onChange={setPeriodTo}
            error={
              endsBeforeStart
                ? 'Choose a date on or after the day it began.'
                : error?.fieldError('periodTo')
            }
            disabled={saving}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="voluntary-work-number-of-hours"
            label="Number of hours"
            type="number"
            min={0}
            step="1"
            /*
              Optional, and the description says why rather than leaving a required-looking
              field blank: an hours total nobody counted is a real state of a 201 file, and
              storing 0 for it would claim the volunteer served none.
            */
            description="Total hours rendered. Leave blank if nobody recorded them — that is not the same as none."
            value={numberOfHours}
            onChange={(e) => setNumberOfHours(e.target.value)}
            error={
              !hoursIsNumber
                ? 'Enter a whole number of hours.'
                : !hoursNotNegative
                  ? 'Enter a number of hours of 0 or more.'
                  : error?.fieldError('numberOfHours')
            }
            disabled={saving}
          />

          <TextField
            id="voluntary-work-position-or-nature"
            label="Position / nature of work"
            required
            description="The post held, or the kind of work rendered — the form asks for either."
            value={positionOrNature}
            onChange={(e) => setPositionOrNature(e.target.value)}
            error={
              positionOrNature.trim()
                ? error?.fieldError('positionOrNature')
                : 'Enter the position or the nature of the work.'
            }
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}
